import React from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { rgba } from 'polished'
import SVG from 'react-inlinesvg'
import { breakpointUpTo, breakpointFrom } from 'utils/breakpoints'
import { LayoutContentWrapper } from '../LayoutContentWrapper/LayoutContentWrapper'
import { StoreButton } from '../StoreButton/StoreButton'
import { ILink, ILinkGroup, StoreIcon } from 'types/locale'
import { IHomePageLocations } from 'types/home-page'

const StyledWrapper = styled.footer`
  background-color: ${({ theme }) => theme.colors.footerBackground};
  color: ${({ theme }) => theme.colors.primary};
  ${({ theme }) => theme.typography.SmBody3};
  padding-top: 32px;
  padding-bottom: 64px;
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    ${({ theme }) => theme.typography.MedBody3};
    padding-top: 48px;
    padding-bottom: 72px;
  }

  ${({ theme }) => breakpointFrom(theme.breakpoints.lg)} {
    ${({ theme }) => theme.typography.LrgBody3};
    padding-top: 54px;
    padding-bottom: 112px;
  }
`

const StyledLinksGroupsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  & > *:not(:first-child) {
    margin-top: ${({ theme }) => theme.space[4]};
  }
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    & > *:not(:first-child) {
      margin-top: 0;
    }
    flex-direction: row;
  }
  > * {
    ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
      width: 25%;
    }
  }
`

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  & > *:not(:first-child) {
    margin-top: ${({ theme }) => theme.space[2]};
  }
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    & > *:not(:first-child) {
      margin-top: ${({ theme }) => theme.space[2]};
    }
  }
`

const StyledLinksGroupHeading = styled.h4`
  ${({ theme }) => theme.typography.SmH4};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.space[3]};
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    ${({ theme }) => theme.typography.LrgH4};
  }
`

const StyledListItem = styled.li`
  display: flex;
  align-items: center;
  a {
    :hover,
    :focus {
      text-decoration: underline;
    }
  }
`

const StyledLocationsLinksList = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${({ theme }) => theme.space[2]};
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const StyledLegalLinks = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${({ theme }) => theme.space[2]};
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const StyledCopyright = styled.span`
  color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => breakpointUpTo(theme.breakpoints.md)} {
    margin-top: 20px;
    margin-bottom: 2px;
  }
`

const StyledSeparator = styled.div`
  height: 1px;
  background-color: ${({ theme }) => rgba(theme.colors.white, 0.12)};
  margin-top: 32px;
  margin-bottom: 32px;
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    margin-top: 36px;
    margin-bottom: 36px;
  }
  ${({ theme }) => breakpointFrom(theme.breakpoints.lg)} {
    margin-top: 48px;
    margin-bottom: 48px;
  }
`
const StyledLink = styled.a`
  display: inline-flex;
  align-items: center;
`

const StyledIcon = styled(SVG)`
  margin-right: 14px;
`

const StyledStoreLinksGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  & > *:not(:first-child) {
    margin-left: 16px;
  }
  ${({ theme }) => breakpointFrom(theme.breakpoints.md)} {
    flex-direction: column;
    & > *:not(:first-child) {
      margin-left: 0;
      margin-top: ${({ theme }) => theme.space[3]};
    }
  }
`

const StyledStoreButton = styled(StoreButton)`
  svg {
    ${({ theme }) => breakpointUpTo(theme.breakpoints.sm)} {
      height: 42px;
      width: auto;
    }
  }
`

export type FooterProps = {
  linksGroups?: ILinkGroup[]
  locationsData?: IHomePageLocations
  legalLinks: ILink[]
  storesIcons?: StoreIcon[]
}

export const Footer = ({
  linksGroups,
  locationsData,
  legalLinks,
  storesIcons,
}: FooterProps) => {
  return (
    <StyledWrapper>
      <LayoutContentWrapper>
        {linksGroups && (
          <>
            <StyledLinksGroupsWrapper>
              {linksGroups?.map((linksGroup) => {
                return (
                  <div key={linksGroup.id}>
                    <StyledLinksGroupHeading>
                      {linksGroup.groupName}
                    </StyledLinksGroupHeading>
                    <StyledList>
                      {linksGroup.links.map(({ id, text, url, icon }) => {
                        return (
                          <StyledListItem key={id}>
                            <StyledLink href={url}>
                              {icon && <StyledIcon src={icon} />}
                              {text}
                            </StyledLink>
                          </StyledListItem>
                        )
                      })}
                    </StyledList>
                  </div>
                )
              })}
              <StyledStoreLinksGroup>
                {storesIcons?.map(({ id, ...rest }) => (
                  <StyledStoreButton key={id} {...rest} />
                ))}
              </StyledStoreLinksGroup>
            </StyledLinksGroupsWrapper>
            <StyledSeparator />
          </>
        )}

        {locationsData && (
          <>
            <StyledLinksGroupHeading>
              {locationsData?.locationsFooterSectionHeading}
            </StyledLinksGroupHeading>
            <StyledLocationsLinksList>
              {locationsData?.data.map(({ id, name, path }) => {
                return (
                  <StyledListItem key={id}>
                    <Link to={`/${locationsData.pathname}/${path}`}>
                      {name}
                    </Link>
                  </StyledListItem>
                )
              })}
            </StyledLocationsLinksList>
            <StyledSeparator />
          </>
        )}

        <StyledLegalLinks>
          {legalLinks.map(({ id, text, url }) => {
            return (
              <StyledListItem key={id}>
                <StyledLink href={url}>{text}</StyledLink>
              </StyledListItem>
            )
          })}
          <StyledListItem>
            <StyledCopyright>
              &#169; {new Date().getFullYear()} goBrands, Inc
            </StyledCopyright>
          </StyledListItem>
        </StyledLegalLinks>
      </LayoutContentWrapper>
    </StyledWrapper>
  )
}
